import { SyntheticEvent, useMemo } from 'react'
import { MultiSelect } from '@/components/Select'
import { GridFilterCellProps } from '@progress/kendo-react-grid'

import { OptionsProps } from './types'

export type SelectValue = string | number

export type ItemProps = {
  id: string | number
  value: SelectValue
  label: string | number
}

type MultiSelectFilterCellProps = {
  items: ItemProps[]
  defaultItemName?: string
  options?: OptionsProps
}

export const multiSelectFilterCell =
  ({ items, defaultItemName = 'Select ...', options }: MultiSelectFilterCellProps) =>
  (props: GridFilterCellProps): JSX.Element => (
    <MultiSelectFilterCell
      {...props}
      items={items}
      defaultItemName={defaultItemName}
      options={options}
    />
  )

type MultiSelectFilterCellComponentProps = {
  items: ItemProps[]
  defaultItemName: string
  options?: OptionsProps
} & GridFilterCellProps

const VISIBLE_SELECTED_CHIPS = 1

const MultiSelectFilterCell = (props: MultiSelectFilterCellComponentProps) => {
  const selectedValues = Array.isArray(props.value) ? (props.value as SelectValue[]) : []
  const selectOptions = useMemo(
    () =>
      props.items.map((item) => ({
        key: item.id,
        value: item.value,
        label: item.label,
        text: String(item.label),
      })),
    [props.items]
  )

  const operator = (value: SelectValue, selectedValues: SelectValue[]) => {
    if (selectedValues.length === 0) {
      return true
    }
    return selectedValues.includes(value)
  }

  const handleChange = (values: SelectValue[]) => {
    props.onChange({
      value: values.length > 0 ? values : [],
      operator: props?.options?.operator ? props.options.operator : operator,
      syntheticEvent: undefined as unknown as SyntheticEvent<unknown, Event>,
    })
  }

  return (
    <MultiSelect
      fullWidth
      showSearch
      options={selectOptions}
      value={selectedValues}
      placeholder={props.defaultItemName}
      maxVisibleChips={VISIBLE_SELECTED_CHIPS}
      onChange={(value) =>
        handleChange(
          (Array.isArray(value) ? value : []).filter(
            (selectedValue): selectedValue is SelectValue => selectedValue != null
          )
        )
      }
    />
  )
}
